/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import { CurrentUserActions } from "types/Actions";
import * as ActionTypes from "constants/ActionTypes";

export type AdobeId = string;

export interface EnhanceSpeechLimits {
  fileSizeLimitBytes: number;
  videoFileSizeLimitBytes: number;
  dailyDurationLimitSeconds: number;
  fileDurationLimitSeconds: number;
  pollingTimeoutSeconds: number;
}

export interface CurrentUser {
  hasAdminAccess: boolean;
  hasPremiumAccess: boolean;
  hasFreeStudio: boolean;
  hasAllBrowserStudioAccess: boolean;
  hasBlockedCountryCodeForUserProfile: boolean;
  canCreateAnonymousProjectAccessKeys: boolean;
  studioBackendExportEnabled: boolean;
  studioBackendExportMp3Enabled: boolean;
  studioDownloadOriginalRecordingTracksEnabled: boolean;
  studioRecordingSessionImportEnabled: boolean;
  hasStreamingStudioPlayerAccess: boolean;
  acceptedInterstitial?: true;
  adobeId: AdobeId;
  hasEnhanceSpeechAccess: boolean;
  enhanceSpeechLimits: EnhanceSpeechLimits;
  timezone?: number;
  preferredLanguage?: string;
  isAnonymous: boolean;
  chosenName?: string;
  requestedEarlyAccess?: boolean;
  studioConvertedAudioUploadEnabled: boolean;
  studioVideoUploadEnabled: boolean;
  editSpeakerNameEnabled: boolean;
  postEnhanceStudioNudgeEnabled: boolean;
  studioHeaderUpgradeButtonEnabled: boolean;
  studioTranscriptionSpeakerDiarizationEnabled: boolean;
  esqaTrackMutingEnabled: boolean;
  esqaIndividualTrackDownloadEnabled: boolean;
  studioRecordingV2Enabled: boolean;
  studioRecordingV2WebrtcTestEnabled: boolean;
  sl4wEsqaEnabled: boolean;
  sl4wStudioEnabled: boolean;
  submitFeedbackEnabled: boolean;
  fireflyProTrialToPaidEnabled: boolean;
  hidePlansPage: boolean;
}

export interface CurrentUserState {
  currentUser: CurrentUser | null;
  currentUserLoaded: CurrentUserLoadingState;
}

export enum CurrentUserLoadingState {
  NOT_LOADED = "NOT_LOADED",
  LOADING = "LOADING",
  LOADED = "LOADED",
  FAILED = "FAILED",
}

export default function CurrentUserReducer(
  state: CurrentUserState,
  action: CurrentUserActions
): CurrentUserState {
  switch (action.type) {
    case ActionTypes.SET_CURRENT_USER: {
      let currentUserLoaded: CurrentUserLoadingState =
        CurrentUserLoadingState.NOT_LOADED;
      if (action.currentUser !== null) {
        currentUserLoaded = CurrentUserLoadingState.LOADED;
      }

      return {
        ...state,
        currentUser: action.currentUser,
        currentUserLoaded,
      };
    }
    case ActionTypes.USER_AUTH_FINISHED:
      return {
        ...state,
        currentUserLoaded: CurrentUserLoadingState.LOADED,
      };
    case ActionTypes.USER_AUTH_FAILED:
      return {
        ...state,
        currentUserLoaded: CurrentUserLoadingState.FAILED,
      };
    case ActionTypes.USER_AUTH_STARTED:
      return {
        ...state,
        currentUserLoaded: CurrentUserLoadingState.LOADING,
      };
    case ActionTypes.SET_CURRENT_USER_CHOSEN_NAME:
      if (!state.currentUser) {
        return state;
      }

      return {
        ...state,
        currentUser: {
          ...state.currentUser,
          chosenName: action.chosenName,
        },
      };
  }

  return state;
}
